import { useEffect, useCallback, DependencyList, useState } from 'react';
import { useMounted } from './useMounted';

export type AsyncState<R> =
  | {
      loading: boolean;
      error?: undefined;
      value?: undefined;
    }
  | {
      loading: false;
      error: any;
      value?: undefined;
    }
  | {
      loading: false;
      error?: undefined;
      value: R;
    };

export type AsyncFn<R, Args extends any[]> = [AsyncState<R>, (...args: Args) => Promise<R>];

/**
 * Returns the current state, and a callback to execute the async function.
 *
 * ex:
 * const [userState, doFetchUser] = useAsyncFn<string, [string]>(async (name: string) => {
 *   // will not execute if component unmounts
 *   const user = await fetchUser(name)
 *   return user;
 * }, [])
 *
 * // can safely be used asynchronously without lifecycle concerns
 * setTimeout(() => doFetchUser('example'), 1000);
 */
export const useAsyncFn = <R, Args extends any[]>(
  fn: (...args: Args) => Promise<R>,
  deps: DependencyList = [],
  initialState: AsyncState<R> = { loading: false },
): AsyncFn<R, Args> => {
  const [state, setState] = useState<AsyncState<R>>(initialState);
  const isMounted = useMounted();

  // eslint-disable-next-line require-await
  const callback = useCallback(async (...args: Args) => {
    if (!isMounted()) return;
    setState({ loading: true });
    return fn(...args)
      .then((value: R) => {
        isMounted() && setState({ value, loading: false });
        return value;
      })
      .catch(error => {
        isMounted() && setState({ value: undefined, loading: false, error });
        return error;
      });
  }, deps);

  return [state, callback];
};

/**
 * Similar to useAsyncFn, but will immediately execute the given async function
 * whenever the dependency list changes, and accepts no arguments.
 *
 * Returns the current state (AsyncState<R>).
 *
 * const userState = useAsync<string>(async () => {
 *   // will not execute if component unmoounts
 *   // name is assumed to be a prop.
 *   const user = await fetchUser(name)
 *   return user;
 * }, [name])
 */
export const useAsync = <R = any>(fn: () => Promise<R>, deps: DependencyList = []) => {
  const [state, callback] = useAsyncFn<R, []>(fn, deps);

  useEffect(() => {
    callback();
  }, [callback]);

  return state;
};
